import { useState } from 'react';
import { Stack, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useT } from '../i18n/useT.js';

/**
 * One credential the admin will paste somewhere: shown in full, with a copy
 * button. Truncating an ingest key or a set-password link would defeat the
 * point of showing it.
 */
export function CopyRow({ label, value }: { label: string; value: string }) {
  const common = useT().common;
  const [copied, setCopied] = useState(false);
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 108, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}
      >
        {value}
      </Typography>
      <Tooltip title={copied ? common.copied : common.copy}>
        <IconButton
          size="small"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? (
            <CheckIcon fontSize="small" color="success" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export interface OnboardingCredentialsData {
  projectId: string | null;
  publicKey: string | null;
  apiKey: string | null;
  setupLink?: string;
}

/** The hand-off sheet: what a customer needs to install, and to get in. */
export function OnboardingCredentials({ data }: { data: OnboardingCredentialsData }) {
  const t = useT().replay.credentials;
  return (
    <Stack spacing={1.5}>
      {data.projectId && <CopyRow label={t.projectId} value={data.projectId} />}
      {data.apiKey && <CopyRow label={t.ingestKey} value={data.apiKey} />}
      {data.publicKey && <CopyRow label={t.publicKey} value={data.publicKey} />}
      {data.setupLink && <CopyRow label={t.setupLink} value={data.setupLink} />}
    </Stack>
  );
}
